// =====================================================
// CLIENT DEVICE FINGERPRINT  (10.1)
//
// Builds a STABLE device hash from many browser signals and returns it together
// with the raw signals the server cross-checks (platform, webdriver, etc.). The
// hash is sent with register / login / publish requests; the server records it
// and independently cross-checks the server-visible signals — the client is
// never trusted alone.
//
// Signals combined:
//   canvas hash, WebGL vendor/renderer, audio-context hash, screen resolution +
//   colour depth, device pixel ratio, timezone, language list, platform,
//   hardware concurrency, device memory, font/plugin heuristics, touch support.
//
// The final hash is SHA-256 (Web Crypto) over the concatenated signals, cached
// in localStorage so it is computed once per browser. Everything is wrapped so
// this can NEVER throw into the calling page — a missing signal just degrades
// the hash, it never breaks auth.
// =====================================================

const CACHE_KEY = "deviceFingerprintV2";

// ── WHICH SIGNALS MAY DEFINE THE DEVICE IDENTITY ──────────────
// The hash is the KEY of the one-device-one-account block, so it must survive
// everything a normal person does between two visits. Measured in Chrome:
// changing the page zoom (Ctrl +/-) or moving the window to a monitor with a
// different scaling factor changes devicePixelRatio AND screen.width/height —
// which used to change the hash completely, so the same browser looked like a
// brand-new device and the block had nothing to match on. `localStorage.clear()`
// on logout wipes the cache below, so a recompute happens routinely in normal
// use and MUST reproduce the same value.
//
// So the hash is built ONLY from signals that do not move with zoom, window
// size, monitor, DST or an extension being installed. Everything else is still
// COLLECTED and still sent to the server for fraud scoring (10.1/10.5) — it is
// only excluded from the identity.
//
// Excluded on purpose:
//   pixelRatio      — changes with zoom / display scaling (and carries float
//                     noise: 0.8 reports as 0.800000011920929)
//   screen w/h      — CSS pixels, so they move inversely with zoom
//   timezoneOffset  — flips twice a year on DST
//   pluginsCount    — changes when an extension/plugin is added or removed
//   webdriver       — an automation signal, not an identity
const STABLE_SIGNALS = [
  "canvas",
  "webgl",
  "audio",
  "fonts",
  "colorDepth",
  "timezone",
  "languages",
  "language",
  "platform",
  "userAgentData",
  "hardwareConcurrency",
  "deviceMemory",
  "touch",
  "maxTouchPoints",
];

function safe(fn, fallback = "") {
  try {
    const v = fn();
    return v === undefined || v === null ? fallback : v;
  } catch (_) {
    return fallback;
  }
}

// ── Canvas fingerprint: draw text/shapes and hash the pixel data URL. ──
function canvasSignal() {
  return safe(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("TheBlogsSphere 🔐 fp", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("TheBlogsSphere 🔐 fp", 4, 17);
    return canvas.toDataURL();
  });
}

// ── WebGL vendor + renderer. ──
function webglSignal() {
  return safe(() => {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "";
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = dbg ? gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    return `${vendor}~${renderer}`;
  });
}

// ── Audio-context fingerprint: a short offline render, summed. ──
function audioSignal() {
  return safe(() => {
    const Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!Ctx) return "";
    const ctx = new Ctx(1, 44100, 44100);
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = 10000;
    const comp = ctx.createDynamicsCompressor();
    osc.connect(comp);
    comp.connect(ctx.destination);
    osc.start(0);
    // Synchronous approximation: hash the node configuration (a full offline
    // render is async; the config alone is stable + device-specific enough as
    // one of many signals).
    return `${comp.threshold.value}~${comp.knee.value}~${comp.ratio.value}~${ctx.sampleRate}`;
  });
}

// ── Font heuristic: measure widths of a probe string in several font stacks. ──
function fontSignal() {
  return safe(() => {
    const probes = ["monospace", "sans-serif", "serif", "Arial", "Courier New", "Times New Roman"];
    const span = document.createElement("span");
    span.style.position = "absolute";
    span.style.left = "-9999px";
    span.style.fontSize = "72px";
    span.textContent = "mmmmmmmmmmlli";
    document.body.appendChild(span);
    const widths = probes.map((f) => {
      span.style.fontFamily = f;
      return span.offsetWidth;
    });
    document.body.removeChild(span);
    return widths.join(",");
  });
}

// Collect every signal into a flat object.
function collectSignals() {
  const nav = window.navigator || {};
  const scr = window.screen || {};
  return {
    canvas: canvasSignal(),
    webgl: webglSignal(),
    audio: audioSignal(),
    fonts: fontSignal(),
    screen: safe(() => `${scr.width}x${scr.height}x${scr.colorDepth}`),
    // Colour depth on its own: unlike width/height it does not move with zoom,
    // so it is the part of `screen` the identity can safely use.
    colorDepth: safe(() => String(scr.colorDepth || "")),
    pixelRatio: safe(() => String(window.devicePixelRatio || "")),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    timezoneOffset: safe(() => String(new Date().getTimezoneOffset())),
    languages: safe(() => (nav.languages || [nav.language]).join(",")),
    language: safe(() => nav.language),
    platform: safe(() => nav.platform),
    hardwareConcurrency: safe(() => String(nav.hardwareConcurrency || "")),
    deviceMemory: safe(() => String(nav.deviceMemory || "")),
    pluginsCount: safe(() => String((nav.plugins && nav.plugins.length) || 0)),
    touch: safe(() => String("ontouchstart" in window || (nav.maxTouchPoints || 0) > 0)),
    maxTouchPoints: safe(() => String(nav.maxTouchPoints || 0)),
    // Automation/bot signal the server folds into headless detection (10.5).
    webdriver: safe(() => !!nav.webdriver),
    userAgentData: safe(() => (nav.userAgentData && nav.userAgentData.platform) || ""),
  };
}

// ── SECOND, INDEPENDENT DEVICE KEY ────────────────────────────
// The hash above is derived, so it can still drift when the browser or the GPU
// driver updates and canvas/WebGL output changes. This is a random id minted
// once and then kept, which cannot drift at all. The two cover each other:
//
//   fingerprint drifts (browser update)  → the stored id still matches
//   storage cleared / incognito          → the fingerprint still matches
//
// It is stored in a COOKIE as well as localStorage on purpose: logging out
// calls localStorage.clear() (Navbar.jsx / Settings.jsx), which would wipe a
// localStorage-only id on every logout. A cookie survives that.
const DEVICE_ID_KEY = "deviceIdV1";
const DEVICE_ID_COOKIE = "tbs_device_id";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name) {
  return safe(() => {
    const hit = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.indexOf(name + "=") === 0);
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : "";
  });
}

function writeCookie(name, value) {
  safe(() => {
    document.cookie = `${name}=${encodeURIComponent(value)};max-age=${ONE_YEAR_SECONDS};path=/;SameSite=Lax`;
    return true;
  });
}

function randomId() {
  return safe(() => {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    const b = new Uint8Array(16);
    window.crypto.getRandomValues(b);
    return Array.from(b)
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("");
  }, String(Date.now()) + Math.random().toString(16).slice(2));
}

// Returns the persistent device id, minting and storing one on first sight.
// Always returns a string; "" only if both cookies and localStorage are blocked.
function getDeviceId() {
  let id = readCookie(DEVICE_ID_COOKIE);
  if (!id) id = safe(() => localStorage.getItem(DEVICE_ID_KEY)) || "";
  if (!id) id = randomId();
  // Re-write both every time, so an id that survived in only one of the two
  // stores is restored into the other.
  writeCookie(DEVICE_ID_COOKIE, id);
  safe(() => localStorage.setItem(DEVICE_ID_KEY, id));
  return id;
}

// SHA-256 hex of a string via Web Crypto, with a non-crypto fallback.
async function sha256Hex(str) {
  try {
    if (window.crypto && window.crypto.subtle) {
      const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
      return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch (_) {
    /* fall through */
  }
  // Fallback: djb2 → hex (not cryptographic, but stable). Prefixed so the
  // server can tell it apart.
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return "fb" + h.toString(16).padStart(8, "0");
}

// --------------------------------------------------------------
// getDeviceFingerprint(): returns { deviceHash, deviceId, signals }. The hash
// is cached per browser; the id is persistent (cookie + localStorage).
// Always resolves — never rejects — so callers can `await` it inline safely.
// --------------------------------------------------------------
export async function getDeviceFingerprint() {
  // Resolved outside the cache, so an id restored from the cookie is picked up
  // even when the hash comes from the cache.
  const deviceId = getDeviceId();

  try {
    const cachedRaw = localStorage.getItem(CACHE_KEY);
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw);
      if (cached && cached.deviceHash) return { ...cached, deviceId };
    }
  } catch (_) {
    /* ignore corrupt cache */
  }

  let signals = {};
  let deviceHash = "";
  try {
    signals = collectSignals();
    // Identity = the STABLE signals only (see STABLE_SIGNALS above). The full
    // `signals` bundle is still returned and still sent to the server.
    const material = STABLE_SIGNALS.slice()
      .sort()
      .map((k) => `${k}=${signals[k] === undefined ? "" : signals[k]}`)
      .join("|");
    deviceHash = await sha256Hex(material);
  } catch (_) {
    deviceHash = "";
  }

  const result = { deviceHash, signals };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(result));
  } catch (_) {
    /* storage full/blocked — fine, we just recompute next time */
  }
  return { ...result, deviceId };
}

// Convenience: just the hash string (or "" on failure).
export async function getDeviceHash() {
  const { deviceHash } = await getDeviceFingerprint();
  return deviceHash || "";
}

export default getDeviceFingerprint;
